import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase';
import { doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import './SpotlightDetail.css';

const SpotlightDetail = ({ takeover, refreshTakeover }) => {
  const [file, setFile] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [userInfo, setUserInfo] = useState(null);
  const [triggerId, setTriggerId] = useState(takeover.triggerId || '');

  // Fetch user information
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (takeover && takeover.userUid) {
        const userDoc = await getDoc(doc(db, 'users', takeover.userUid));
        if (userDoc.exists()) {
          setUserInfo(userDoc.data());
        }
      }
    };
    fetchUserInfo();
  }, [takeover]);

  // Approve creative
  const handleApproveCreative = async (creativeKey) => {
    if (!triggerId) {
      alert('Please provide a Trigger ID before approving.');
      return;
    }
    const takeoverRef = doc(db, 'spotlights', takeover.id);
    await updateDoc(takeoverRef, {
      [`creativeDetails.${creativeKey}.status`]: 'Approved',
      triggerId: triggerId, // Save triggerId at the order level
    });
    alert('Creative approved');
    refreshTakeover(); // Refresh the UI to reflect changes
  };

  // Reject creative
  const handleRejectCreative = async (creativeKey) => {
    if (rejectReason === '') {
      alert('Please provide a reason for rejection');
      return;
    }
    const takeoverRef = doc(db, 'spotlights', takeover.id);
    await updateDoc(takeoverRef, {
      [`creativeDetails.${creativeKey}.status`]: 'Rejected',
      [`creativeDetails.${creativeKey}.rejectReason`]: rejectReason,
    });
    alert('Creative rejected');
    setRejectReason('');
    refreshTakeover(); // Refresh the UI to reflect changes
  };

  // Upload new creative
  const handleUploadCreative = async () => {
    if (!file) {
      alert('Please select a file to upload');
      return;
    }

    try {
      const storageRef = ref(storage, `inHouseCreative/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      const preferredAdFormat = takeover.signResolution;

      const takeoverRef = doc(db, 'spotlights', takeover.id);
      await updateDoc(takeoverRef, {
        [`creativeDetails.${preferredAdFormat}`]: {
          croppedUrl: url,
          status: 'Pending Customer Review',
        },
      });

      alert('Creative uploaded successfully');
      refreshTakeover(); // Refresh the UI to reflect changes
    } catch (error) {
      console.error('Error uploading creative:', error);
      alert('Failed to upload creative.');
    }
  };

  // Update takeover status
  const handleUpdateStatus = async () => {
    if (!newStatus) {
      alert('Please enter a status to update');
      return;
    }
    try {
      const takeoverRef = doc(db, 'spotlights', takeover.id);
      await updateDoc(takeoverRef, {
        status: newStatus,
      });
      alert('Status updated successfully');
      setNewStatus('');
      refreshTakeover(); // Refresh the UI to reflect changes
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status.');
    }
  };

  if (!takeover) {
    return <div>Loading...</div>;
  }

  const formattedPrice = takeover.totalPrice != null ? `$${takeover.totalPrice.toFixed(2)}` : 'N/A';

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Approved': return { backgroundColor: 'green', color: 'white', padding: '4px 8px', borderRadius: '4px' };
      case 'Rejected': return { backgroundColor: 'red', color: 'white', padding: '4px 8px', borderRadius: '4px' };
      case 'Canceled': return { backgroundColor: 'gold', color: 'black', padding: '4px 8px', borderRadius: '4px' };
      case 'Pending': return { backgroundColor: 'gray', color: 'white', padding: '4px 8px', borderRadius: '4px' };
      default: return { backgroundColor: 'blue', color: 'white', padding: '4px 8px', borderRadius: '4px' };
    }
  };

  return (
    <div className="takeover-detail-container">
      <h3>{takeover.signName}</h3>
      <p><strong>Address:</strong> {takeover.address || 'N/A'}</p>
      <p><strong>DMA:</strong> {takeover.dma || 'N/A'}</p>
      <p><strong>Total Price:</strong> {formattedPrice}</p>
      <p><strong>Date:</strong> {new Date(takeover.date).toLocaleDateString() || 'N/A'}</p>

      {/* Time Slots */}
      <h4>Time Slots</h4>
      <ul>
        {takeover.timeSlots && takeover.timeSlots.length > 0 ? (
          takeover.timeSlots.map((slot, index) => <li key={index}>{slot}</li>)
        ) : (
          <p>No time slots available.</p>
        )}
      </ul>

      {/* Status */}
      <p>
        <strong>Status:</strong>{' '}
        <span style={getStatusStyle(takeover.status)}>{takeover.status || 'No Status'}</span>
      </p>

      {/* Activated Indicator */}
      <p>
        <strong>Activated:</strong>{' '}
        {takeover.activated ? (
          <span style={{ color: 'green' }}>Activated</span>
        ) : (
          <span style={{ color: 'red' }}>Not Activated</span>
        )}
      </p>

      {/* Trigger ID */}
      <div className="trigger-id-section">
        {takeover.triggerId ? (
          <p><strong>Trigger ID:</strong> {takeover.triggerId}</p>
        ) : (
          <div>
            <input
              type="text"
              placeholder="Enter Trigger ID"
              value={triggerId}
              onChange={(e) => setTriggerId(e.target.value)}
            />
            <button onClick={() => setTriggerId(triggerId)}>Save Trigger ID</button>
          </div>
        )}
      </div>

      {userInfo && (
        <div className="user-info">
          <h4>User Information</h4>
          <p><strong>Name:</strong> {userInfo.name}</p>
          <p><strong>Email:</strong> {userInfo.email}</p>
        </div>
      )}

      {/* Creatives */}
      <h4>Creatives</h4>
      {takeover.creativeDetails ? (
        Object.keys(takeover.creativeDetails).map((key, index) => {
          const creative = takeover.creativeDetails[key];
          const isVideo = creative.croppedUrl.includes('mp4') || creative.croppedUrl.includes('video');
          return (
            <div key={index} className="creative-detail">
              {isVideo ? (
                <video controls style={{ width: '200px', height: 'auto' }}>
                  <source src={creative.croppedUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={creative.croppedUrl}
                  alt="Creative"
                  style={{ width: '200px', height: 'auto' }}
                />
              )}
              <p><strong>Status:</strong> {creative.status}</p>
              <div className="creative-actions">
                <button onClick={() => handleApproveCreative(key)}>Approve</button>
                <button onClick={() => handleRejectCreative(key)}>Reject</button>
                <input
                  type="text"
                  placeholder="Reason for rejection"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </div>
            </div>
          );
        })
      ) : (
        <p>No creative details available.</p>
      )}

      {/* Update Status */}
      <div className="update-status">
        <h4>Update Takeover Status</h4>
        <input
          type="text"
          placeholder="Enter new status (e.g., Approved)"
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
        />
        <button onClick={handleUpdateStatus}>Update Status</button>
      </div>
    </div>
  );
};

export default SpotlightDetail;
